import { useEffect, useRef, useCallback } from 'react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { type AdunitSize, type Dimensions } from '@app/types';
import { getConstrainedSize } from '@app/utils';
import { useWixSdk } from './use-wix-sdk';

const LOG_PREFIX = '[useLimitResize]';
const DEBOUNCE_MS = 400;

interface UseLimitResizeProps {
  sizeType?: AdunitSize;
  fixedDimensions?: Dimensions;
}

export const useLimitResize = ({
  sizeType,
  fixedDimensions,
}: UseLimitResizeProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { resizeComponent, getViewMode } = useWixSdk();
  const { isMobile } = useEnvironment();
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const isInEditor = getViewMode() !== 'site';

  /**
   * Check current size and apply constraints if needed.
   */
  const enforceConstraints = useCallback(() => {
    const { innerWidth, innerHeight } = window;
    const { needsResize, target } = getConstrainedSize(
      innerWidth,
      innerHeight,
      sizeType,
      fixedDimensions,
      isMobile,
    );

    if (needsResize && target) {
      resizeComponent(target.width, target.height);
    }
  }, [sizeType, fixedDimensions, isMobile, resizeComponent]);

  /**
   * Debounced resize handler - waits for user to finish dragging.
   */
  const handleResize = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(enforceConstraints, DEBOUNCE_MS);
  }, [enforceConstraints]);

  // Main effect: enforce constraints on mount and resize
  useEffect(() => {
    if (!isInEditor) {
      return;
    }

    // Enforce on mount (with delay to let editor initialize)
    const mountTimeout = setTimeout(enforceConstraints, DEBOUNCE_MS);

    // Enforce on resize
    window.addEventListener('resize', handleResize, false);

    return () => {
      clearTimeout(mountTimeout);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      window.removeEventListener('resize', handleResize, false);
    };
  }, [isInEditor, enforceConstraints, handleResize]);

  return containerRef;
};
